import {
   Component,
   EventEmitter,
   Input,
   OnChanges,
   OnInit,
   Output,
   ViewChild
} from '@angular/core';
import { DateTime } from 'luxon';
import { Task } from '../../domain/task';
import { Comment } from '../../domain/comment';
import { TaskService } from '../../services/task.service';
import { Assignment } from '../../domain/assignment';
import { RowButton } from '../models/row-button';
import { ConfirmationService, MessageService } from 'primeng/api';
import { WorkflowFile } from '../../domain/workflow-file';
import { HttpErrorResponse } from '@angular/common/http';
import { WorkflowType } from '../../domain/enums/workflow-type';
import { PhobosTaskInstanceService } from '../../services/phobos-task-instance.service';
import { DatePropertyTimezonePipe } from '../../pipes/date-property-timezone.pipe';
import { UploadFile } from '../../domain/uploadfile';
import { FileUpload } from 'primeng/fileupload';
import { AiDataTaskUtils } from '../../util/ai-data-task-utils';
import { ModelSummary } from '../../domain/Ai/model-summary';
import { TaskStage } from '../../domain/task-stage';
import { PhobosTaskService } from '../../services/phobos-task.service';
import { UserStoreService } from '../../services/stores/user-store.service';
import { User } from '../../domain/user';
import { DataStoreService } from '../../services/data-store.service';
import { RefreshDataState } from '../../domain/refresh-data-state';

@Component({
   selector: 'app-document-side-panel',
   templateUrl: './document-side-panel.component.html',
   styleUrls: ['./document-side-panel.component.scss']
})
export class DocumentSidePanelComponent implements OnChanges, OnInit {
   @ViewChild('subFileUploadForm')
   uploadForm: FileUpload | undefined;

   @Input() currentTask: Task;

   @Input() isPhobos: boolean = false;

   @Input() hasPrimaryDocument: boolean;

   @Input() aiData?: ModelSummary;

   @Input() canSendBackToPreviousGroup: boolean = true;

   @Output() addComment: EventEmitter<any> = new EventEmitter();

   @Output() taskInstanceRefreshRequested = new EventEmitter<void>();

   comments: Comment[];
   assignments: Assignment[];
   buttons: RowButton[];
   currentSecondaryFile?: WorkflowFile;
   secondaryPdfContainer: string;
   supportingFiles: WorkflowFile[];
   supportingFilesIndex: number;
   uploadedFiles: UploadFile[] = [];
   commentsView: boolean;
   supportingFilesView: boolean;
   detailsView: boolean;
   showPanelPadding: string;
   isDropdownVisible: boolean = false;
   assignView: boolean = false;
   phobosPropertyId?: number;
   protected readonly DatePropertyTimezonePipe = DatePropertyTimezonePipe;
   currentWorkflowGroupName?: string;
   currentUser?: User;

   taskStages: TaskStage[] = [];
   filteredStages: TaskStage[] = [];
   selectedStage?: TaskStage;

   isLoadingStages: boolean = false;
   isAssigning: boolean = false;
   private isCompletedOnServer = false;
   private isAssignedToUserOnServer = false;

   constructor(
      private taskService: TaskService,
      private messageService: MessageService,
      private formTaskService: PhobosTaskInstanceService,
      private phobosTaskService: PhobosTaskService,
      private confirmationService: ConfirmationService,
      private userStore: UserStoreService,
      private dataStoreService: DataStoreService
   ) {
      this.currentTask = new Task();
      this.comments = [];
      this.assignments = [];
      this.supportingFiles = [];
      this.hasPrimaryDocument = false;
      this.currentSecondaryFile = new WorkflowFile();
      this.secondaryPdfContainer = 'sub-file-container';
      this.buttons = [
         new RowButton(
            'add_comment',
            'Add Comment',
            'fal fa-plus',
            ['p-button-rounded', 'p-mr-2', 'p-mb-2'],
            ['p-col', 'p-text-right']
         )
      ];
      this.commentsView = true;
      this.supportingFilesView = false;
      this.detailsView = false;
      this.supportingFilesIndex = 0;
      this.showPanelPadding = 'h-1rem';
   }

   get panelHeading(): string {
      if (this.supportingFilesView) {
         if (this.currentSecondaryFile) {
            return `Supporting File:  ${this.currentSecondaryFile.fileName}`;
         }
         return 'Supporting Files';
      } else if (this.detailsView) {
         return 'Details';
      }
      return 'Comments';
   }

   ngOnInit(): void {
      this.getAssignments();
      this.getComments();
      this.getSupportingFiles();
      this.userStore.currentUser.subscribe((u) => {
         this.currentUser = u;
         this.evaluateSendBackAvailability();
      });
   }

   ngOnChanges(): void {
      this.showCommentsView();
      this.clear();
      this.getAssignments();
      this.getComments();
      this.getSupportingFiles();
      this.loadTaskStagesForSendBackAvailability();
   }

   toggleDropdown(): void {
      if (this.supportingFiles.length !== 0) {
         this.isDropdownVisible = !this.isDropdownVisible;
      }
   }

   getSupportingFiles() {
      // Resets dropdown when user scrolls through tasks in task header.
      this.isDropdownVisible = false;
      if (this.currentTask.processId) {
         if (this.isPhobos || this.currentTask.type == WorkflowType.Form) {
            this.formTaskService
               .getSupportingFilesByPhobosTaskInstance(this.currentTask.processId)
               .subscribe(
                  (files) => {
                     this.supportingFiles = files;
                     if (this.supportingFiles.length > 0) {
                        this.currentSecondaryFile =
                           this.supportingFiles[this.supportingFilesIndex];
                     }
                  },
                  (e: HttpErrorResponse) => {
                     console.error(e);
                  }
               );
         } else {
            this.taskService
               .getSupportingFilesByProcessId(this.currentTask.processId)
               .subscribe(
                  (files) => {
                     this.supportingFiles = files;
                     if (this.supportingFiles.length > 0) {
                        this.currentSecondaryFile =
                           this.supportingFiles[this.supportingFilesIndex];
                     }
                  },
                  (e: HttpErrorResponse) => {
                     console.error(e);
                  }
               );
         }
      }
   }

   getComments() {
      if (this.currentTask.processId) {
         switch (this.currentTask.type) {
            case WorkflowType.Form:
               this.formTaskService
                  .getCommentsForPhobosTaskInstance(this.currentTask.processId)
                  .subscribe((comments: Comment[]) => {
                     this.comments = comments;
                  });
               break;
            default:
               this.taskService
                  .getCommentsForWorkflowByProcessId(this.currentTask.processId)
                  .subscribe((comments: Comment[]) => {
                     this.comments = comments;
                     const aiComments = AiDataTaskUtils.GenerateAiCommentsByModel(
                        this.aiData
                     );
                     this.comments = this.comments.concat(aiComments);
                  });
               break;
         }
      }
   }

   onButtonRowClick(id: string) {
      switch (id) {
         case 'supporting_doc':
            break;
         case 'add_comment':
            this.addComment.emit();
            break;
         default:
            break;
      }
   }

   getAssignments() {
      if (this.currentTask.processId) {
         if (this.isPhobos || this.currentTask.type == WorkflowType.Form) {
            this.formTaskService
               .getPhobosTaskInstanceAssignments(this.currentTask.processId)
               .subscribe((fts) => {
                  this.assignments = fts.map((fta) => {
                     const assignmentResult = new Assignment();
                     assignmentResult.id = fta.id;
                     assignmentResult.assignee = `${fta.claimedBy?.firstName} ${fta.claimedBy?.lastName}`;
                     assignmentResult.workflowGroup = fta.group?.displayName ?? '';
                     assignmentResult.displayName = fta.group?.displayName ?? '';
                     assignmentResult.assigneeDisplay = fta.claimedBy
                        ? fta.claimedBy.email
                        : (fta.group?.displayName ?? '');
                     assignmentResult.finishDateTime = fta.completedDate;
                     assignmentResult.startDateTime = fta.createdDate;
                     assignmentResult.claimDateTime = fta.claimedDate;
                     return assignmentResult;
                  });
                  this.assignments = this.sortedAssignments(this.assignments);
                  this.currentWorkflowGroupName = this.getCurrentWorkflowGroupName(
                     this.assignments
                  );
                  this.evaluateSendBackAvailability();
               });
         } else {
            this.taskService
               .getAssignments(this.currentTask.processId)
               .subscribe((assignments) => {
                  const results = assignments.filter((assignment) => {
                     if (assignment.finishDateTime) {
                        const regexp = new RegExp(
                           /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                        );
                        return regexp.test(assignment.assignee);
                     }
                     return false;
                  });
                  this.assignments = this.sortedAssignments(results);
                  this.currentWorkflowGroupName = this.getCurrentWorkflowGroupName(
                     this.assignments
                  );
               });
         }
      }
   }

   private evaluateSendBackAvailability(): void {
      this.canSendBackToPreviousGroup = false;

      if (this.currentTask?.isComplete || this.isCompletedOnServer) {
         this.assignView = false;
         return;
      }

      if (!this.currentTask?.isAssignedToUser || !this.isAssignedToUserOnServer) {
         this.assignView = false;
         return;
      }

      if (!this.currentTask?.isAssignedToUser) {
         this.assignView = false;
         return;
      }

      if (!this.isPhobos) return;
      if (!this.taskStages?.length) return;
      if (!this.currentUser?.groups?.length) return;
      if (!this.currentWorkflowGroupName) return;

      // 1) last stage group id
      const maxStage = Math.max(...this.taskStages.map((s) => s.stage ?? 0));
      const lastStageGroupId = this.taskStages.find(
         (s) => (s.stage ?? 0) === maxStage
      )?.groupId;
      if (!lastStageGroupId) return;

      // 2) current workflow group id (by matching name -> stage)
      const currentName = (this.currentWorkflowGroupName ?? '').trim().toLowerCase();
      const currentGroupId = this.taskStages.find(
         (s) => (s.groupName ?? '').trim().toLowerCase() === currentName
      )?.groupId;

      if (!currentGroupId) return;

      // 3) checks
      const workflowIsInLastStage = currentGroupId === lastStageGroupId;
      const userIsInLastStageGroup = this.currentUser.groups.some(
         (g) => g.id === lastStageGroupId
      );

      this.canSendBackToPreviousGroup =
         workflowIsInLastStage && userIsInLastStageGroup;

      // optional safety: if it became false while open, close the panel
      if (!this.canSendBackToPreviousGroup) {
         this.assignView = false;
      }
   }

   sortedAssignments(assignments: Assignment[]): Assignment[] {
      assignments.sort((a, b) => {
         const aDateTime = DateTime.fromSQL(a.finishDateTime);
         const bDateTime = DateTime.fromSQL(b.finishDateTime);
         if (aDateTime < bDateTime) {
            return -1;
         }
         if (aDateTime > bDateTime) {
            return 1;
         }
         return 0;
      });
      return assignments;
   }

   clear(): void {
      this.currentSecondaryFile = undefined;
      this.supportingFilesIndex = 0;
      this.comments = [];
      this.supportingFiles = [];
      this.assignments = [];
      this.uploadForm?.clear();

      this.isCompletedOnServer = false;
      this.taskStages = [];
      this.filteredStages = [];
      this.selectedStage = undefined;
      this.phobosPropertyId = undefined;
      this.currentWorkflowGroupName = undefined;
   }

   onUploadSupportDoc($event: any) {
      if ($event.files) {
         const uploadUserMessage = () => {
            this.messageService.add({
               severity: 'success',
               summary: 'Supporting File Uploaded for Task',
               detail: `${this.currentTask.processId} file has been uploaded`
            });
         };
         for (const file of $event.files) {
            if (this.isPhobos || this.currentTask.type == WorkflowType.Form) {
               this.formTaskService
                  .uploadSupportingFile(this.currentTask.processId, file)
                  .subscribe((files) => {
                     this.supportingFiles = files;
                     uploadUserMessage();
                     this.setCurrentSupportingFile(true);
                     this.uploadForm?.clear();
                  });
            } else {
               this.taskService
                  .uploadSupportingFile(this.currentTask.processId, file)
                  .subscribe((files) => {
                     this.supportingFiles = files;
                     uploadUserMessage();
                     this.setCurrentSupportingFile(true);
                     this.uploadForm?.clear();
                  });
            }
         }
      }
   }

   setCurrentSupportingFile(reset: boolean = false) {
      if (reset) {
         this.supportingFilesIndex = 0;
      }
      this.currentSecondaryFile = this.supportingFiles[this.supportingFilesIndex];
   }

   setCurrentSupportingFileFromDropdown(i: number) {
      this.showSupportingFilesView();
      this.supportingFilesIndex = i;
      this.setCurrentSupportingFile();
      this.isDropdownVisible = false;
   }

   showCommentsView() {
      this.commentsView = true;
      this.supportingFilesView = false;
      this.detailsView = false;
      this.assignView = false;
   }

   showSupportingFilesView() {
      this.commentsView = false;
      this.supportingFilesView = true;
      this.detailsView = false;
      this.assignView = false;

      // Load initial supporting file by index.
      if (this.supportingFiles.length > 0) {
         this.setCurrentSupportingFile();
      }
   }

   showDetailsView() {
      this.commentsView = false;
      this.supportingFilesView = false;
      this.detailsView = true;
      this.assignView = false;
   }

   showAssignView() {
      if (!this.isPhobos) {
         return;
      }
      if (!this.canSendBackToPreviousGroup) return;

      this.commentsView = false;
      this.supportingFilesView = false;
      this.detailsView = false;
      this.assignView = true;

      this.initAssignTab();
   }

   private loadTaskStagesForSendBackAvailability(): void {
      if (!this.isPhobos) {
         this.taskStages = [];
         this.canSendBackToPreviousGroup = false;
         return;
      }

      const taskInstanceId = this.currentTask?.processId;
      if (!taskInstanceId) return;

      this.formTaskService.getPhobosTaskInstance(taskInstanceId).subscribe({
         next: (instance) => {
            console.log('[DOCSIDEPANEL]', instance);
            this.phobosPropertyId = instance.propertyId;

            // ADD: completed check from server
            this.isCompletedOnServer = !!instance.completedDate;
            this.isAssignedToUserOnServer = instance.isAssignedToUser;

            if (this.isCompletedOnServer || !this.isAssignedToUserOnServer) {
               this.canSendBackToPreviousGroup = false;
               this.assignView = false;
               this.selectedStage = undefined;
               return; // IMPORTANT: do NOT load stages if completed
            }

            this.phobosTaskService
               .getTaskStages(instance.taskId, instance.propertyId)
               .subscribe({
                  next: (stages) => {
                     this.taskStages = stages ?? [];
                     this.evaluateSendBackAvailability();
                  },
                  error: (e) => {
                     console.error(e);
                     this.taskStages = [];
                     this.canSendBackToPreviousGroup = false;
                  }
               });
         },
         error: (e) => {
            console.error(e);
            this.taskStages = [];
            this.canSendBackToPreviousGroup = false;
         }
      });
   }

   initAssignTab() {
      if (!this.isPhobos) return;

      // ADD: hard stop if reviewed/completed
      if (
         this.currentTask?.isComplete ||
         this.isCompletedOnServer ||
         !this.currentTask?.isAssignedToUser
      ) {
         this.canSendBackToPreviousGroup = false;
         this.assignView = false;
         return;
      }

      const taskInstanceId = this.currentTask?.processId;
      if (!taskInstanceId) return;

      // reset state
      this.isLoadingStages = true;
      this.taskStages = [];
      this.filteredStages = [];
      this.selectedStage = undefined;
      this.phobosPropertyId = undefined;

      // 1) get instance so we know propertyId + taskId (template id)
      this.formTaskService.getPhobosTaskInstance(taskInstanceId).subscribe({
         next: (instance) => {
            this.phobosPropertyId = instance.propertyId;

            // 2) load stages for dropdown
            this.phobosTaskService
               .getTaskStages(instance.taskId, instance.propertyId)
               .subscribe({
                  next: (stages) => {
                     this.taskStages = stages ?? [];
                     const maxStage = Math.max(
                        ...this.taskStages.map((s) => s.stage ?? 0)
                     );
                     this.filteredStages = this.taskStages.filter(
                        (s) => (s.stage ?? 0) < maxStage
                     );
                     this.isLoadingStages = false;
                  },
                  error: (e) => {
                     console.error(e);
                     this.isLoadingStages = false;
                     this.messageService.add({
                        severity: 'error',
                        summary: 'Unable to load stages',
                        detail: 'Could not fetch task stages.'
                     });
                  }
               });
         },
         error: (e) => {
            console.error(e);
            this.isLoadingStages = false;
            this.messageService.add({
               severity: 'error',
               summary: 'Unable to load task instance',
               detail: 'Could not fetch Phobos task instance details.'
            });
         }
      });
   }

   onTaskStageSelected(stage: TaskStage) {
      this.selectedStage = stage;
   }

   confirmAssignBack() {
      if (!this.selectedStage?.groupId) return;
      if (!this.phobosPropertyId) return;

      this.confirmationService.confirm({
         header: 'Confirm',
         message: `Send this workflow back to "${this.selectedStage.groupName ?? 'the selected group'}"?<br/>We recommend adding a comment to provide context.`,
         icon: 'pi pi-exclamation-triangle',
         acceptLabel: 'Yes',
         rejectLabel: 'Cancel',
         accept: () => this.assignBackToSelectedGroup()
      });
   }

   assignBackToSelectedGroup() {
      const taskInstanceId = this.currentTask?.processId;
      const groupId = this.selectedStage?.groupId;
      const propertyId = this.phobosPropertyId;

      if (!taskInstanceId || !groupId || !propertyId) return;

      this.isAssigning = true;

      this.formTaskService
         .assignPhobosTaskInstance(taskInstanceId, groupId, propertyId)
         .subscribe({
            next: () => {
               this.isAssigning = false;

               const groupName = this.selectedStage?.groupName ?? 'selected group';

               // hide Send Back UI
               this.assignView = false;
               this.selectedStage = undefined;
               this.canSendBackToPreviousGroup = false;
               this.showCommentsView();

               // refresh side panel data
               this.getAssignments();

               // refresh dashboard task lists
               const refresh = new RefreshDataState();
               refresh.ClaimedTasks = true;
               refresh.UnclaimedTasks = true;
               refresh.MyTasks = true;
               refresh.MyTasksMonthly = true;
               this.dataStoreService.refreshTaskData(refresh);
               this.taskInstanceRefreshRequested.emit();

               this.messageService.add({
                  severity: 'success',
                  summary: 'Assigned',
                  detail: `Workflow sent back to ${groupName}.`
               });
            },
            error: (e) => {
               console.error(e);
               this.isAssigning = false;
               this.messageService.add({
                  severity: 'error',
                  summary: 'Assignment failed',
                  detail: 'Could not assign workflow to the selected group.'
               });
            }
         });
   }

   private getCurrentWorkflowGroupName(
      assignments: Assignment[]
   ): string | undefined {
      if (!assignments || assignments.length === 0) return undefined;

      // Prefer unfinished assignment(s)
      const open = assignments.filter((a) => !a.finishDateTime);

      if (open.length === 0) {
         // fallback: last assignment if everything is completed
         return assignments[assignments.length - 1]?.workflowGroup;
      }

      // If multiple open assignments exist, choose the latest by startDateTime
      open.sort((a, b) => {
         const aDt = DateTime.fromISO(a.startDateTime || '');
         const bDt = DateTime.fromISO(b.startDateTime || '');
         if (aDt < bDt) return -1;
         if (aDt > bDt) return 1;
         return 0;
      });

      return open[open.length - 1]?.workflowGroup;
   }

   public refreshSendBackAvailability(): void {
      this.loadTaskStagesForSendBackAvailability();
   }
}
